import React, { useCallback, useEffect, useState } from 'react'
import { BookOpen, ExternalLink, ListChecks, Trash2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card'
const MAX_SAVED_RECIPES = 35
const SAVED_RECIPE_IMAGES_BUCKET = 'saved-recipe-images'

const getFunctionErrorMessage = async (error, fallbackMessage) => {
  if (error?.context?.json) {
    try {
      const body = await error.context.json()
      if (body?.error) return body.error
    } catch {
      // Fall back to the default message below.
    }
  }

  return error?.message || fallbackMessage
}

const SavedRecipes = () => {
  const { user, userProfile } = useAuth()
  const [recipes, setRecipes] = useState([])
  const [recipeImageUrls, setRecipeImageUrls] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [removingRecipeIds, setRemovingRecipeIds] = useState(new Set())
  const isPremium = userProfile?.tier === 'premium'

  const getSavedRecipes = useCallback(async () => {
    if (!user?.id || !isPremium) {
      setRecipes([])
      setRecipeImageUrls({})
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    const { data, error: savedRecipesError } = await supabase
      .from('saved_recipes')
      .select('id,title,calories,ingredients,recipe_url,image_path,source_image_url,created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (savedRecipesError) {
      console.error('Error loading saved recipes:', savedRecipesError)
      setError('Unable to load saved recipes. Please try again.')
      setRecipes([])
      setRecipeImageUrls({})
    } else {
      const savedRecipes = data || []
      const signedImageEntries = await Promise.all(savedRecipes.map(async (recipe) => {
        if (!recipe.image_path) {
          return [recipe.id, recipe.source_image_url || '']
        }

        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from(SAVED_RECIPE_IMAGES_BUCKET)
          .createSignedUrl(recipe.image_path, 60 * 60)

        if (signedUrlError) {
          console.error('Error creating saved recipe image URL:', signedUrlError)
          return [recipe.id, recipe.source_image_url || '']
        }

        return [recipe.id, signedUrlData?.signedUrl || recipe.source_image_url || '']
      }))

      setRecipes(savedRecipes)
      setRecipeImageUrls(Object.fromEntries(signedImageEntries.filter(([, imageUrl]) => imageUrl)))
    }

    setLoading(false)
  }, [isPremium, user?.id])

  useEffect(() => {
    getSavedRecipes()
  }, [getSavedRecipes])

  const handleRemoveRecipe = async (recipeId) => {
    if (!user?.id || !recipeId) return

    setRemovingRecipeIds(previous => {
      const next = new Set(previous)
      next.add(recipeId)
      return next
    })

    const { error: removeError } = await supabase.functions.invoke('delete-saved-recipe', {
      body: {
        recipe_id: recipeId
      }
    })

    if (removeError) {
      console.error('Error removing saved recipe:', removeError)
      setError(await getFunctionErrorMessage(removeError, 'Unable to remove saved recipe. Please try again.'))
    } else {
      setRecipes(previous => previous.filter(recipe => recipe.id !== recipeId))
      setRecipeImageUrls(previous => {
        const next = { ...previous }
        delete next[recipeId]
        return next
      })
    }

    setRemovingRecipeIds(previous => {
      const next = new Set(previous)
      next.delete(recipeId)
      return next
    })
  }

  if (loading) {
    return (
      <div className="px-4 text-center text-sm text-white sm:text-lg">
        Loading your saved recipes...
      </div>
    )
  }

  return (
    <main className="mx-auto max-w-6xl px-3 sm:px-4">
      <div className="relative mb-5 text-center text-white sm:mb-8">
        {recipes.length > 0 && (
          <div className="mb-3 text-left sm:mb-4">
            <span className="inline-flex rounded-full border border-white/30 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white shadow-modern backdrop-blur-md sm:px-4 sm:py-2 sm:text-sm">
              Saved Recipes {recipes.length}/{MAX_SAVED_RECIPES}
            </span>
          </div>
        )}
        <h1 className="mb-1 text-2xl font-semibold sm:mb-2 sm:text-3xl">Saved Recipes</h1>
        <p className="text-sm text-white/85 sm:text-base">Your Recipe Collection For Later.</p>
      </div>

      {error && (
        <div className="mx-auto mb-4 max-w-2xl rounded border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700 sm:mb-6 sm:px-4 sm:py-3 sm:text-sm">
          {error}
        </div>
      )}

      {recipes.length === 0 ? (
        <div className="mx-auto max-w-2xl rounded-lg bg-white p-5 text-center shadow-lg sm:p-8">
          <h2 className="mb-2 text-xl font-semibold text-brand sm:mb-3 sm:text-2xl">No saved recipes yet</h2>
          <p className="text-sm text-gray-700 sm:text-base">
            Search for a recipe and use the star icon on a recipe card to save it here.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
          {recipes.map(recipe => (
            <Card key={recipe.id} className="mx-auto flex h-[520px] w-full max-w-[340px] flex-col overflow-hidden rounded-2xl sm:mx-0 sm:h-auto sm:max-w-none sm:rounded-lg">
              <CardHeader className="shrink-0 space-y-0.5 p-3.5 pb-1.5 sm:space-y-1.5 sm:p-6">
                <CardTitle className="line-clamp-2 text-[15px] leading-snug sm:text-xl sm:leading-tight">{recipe.title}</CardTitle>
                <CardDescription className="text-[11px] font-semibold text-brand sm:text-sm">
                  Total Calories: {recipe.calories}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex min-h-0 flex-1 flex-col px-3.5 pt-0 sm:p-6 sm:pt-0">
                <div className="mb-1 flex shrink-0 justify-center sm:mb-3">
                  <h2 className="inline-flex items-center justify-center gap-1 rounded-full bg-orange-50 px-2.5 py-0.5 text-[11px] font-semibold text-orange-600 shadow-sm ring-1 ring-orange-100 sm:gap-2 sm:px-5 sm:py-2 sm:text-lg">
                    <BookOpen className="h-3 w-3 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.25} />
                    Recipe
                  </h2>
                </div>
                <div className="mb-1.5 flex shrink-0 justify-center sm:mb-3">
                  <h2 className="inline-flex items-center justify-center gap-1 rounded-full bg-orange-50 px-2.5 py-0.5 text-[11px] font-semibold text-orange-600 shadow-sm ring-1 ring-orange-100 sm:gap-2 sm:px-5 sm:py-2 sm:text-lg">
                    <ListChecks className="h-3 w-3 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.25} />
                    Ingredients
                  </h2>
                </div>
                <ul className="min-h-0 flex-1 list-inside list-disc space-y-0.5 overflow-y-auto text-left sm:flex-none sm:space-y-1 sm:overflow-visible">
                  {(recipe.ingredients || []).map(ingredient => (
                    <li key={ingredient} className="text-[11px] leading-snug text-gray-700 sm:text-sm sm:leading-relaxed">
                      {ingredient}
                    </li>
                  ))}
                </ul>
              </CardContent>

              {recipeImageUrls[recipe.id] && (
                <div className="shrink-0 px-3.5 pb-2.5 sm:px-6 sm:pb-4">
                  <img
                    src={recipeImageUrls[recipe.id]}
                    alt={`${recipe.title} recipe`}
                    loading="lazy"
                    className="h-[156px] w-full rounded-lg object-cover shadow-sm sm:h-44 sm:rounded-md"
                  />
                </div>
              )}

              <CardFooter className="mt-auto flex shrink-0 gap-2 p-3.5 pt-0 sm:gap-3 sm:p-6 sm:pt-0">
                <Button asChild className="h-8 flex-1 rounded-lg text-[11px] sm:h-10 sm:rounded-md sm:text-sm">
                  <a
                    href={recipe.recipe_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap sm:gap-2"
                  >
                    <span className="whitespace-nowrap">See Full Recipe</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden="true" />
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={removingRecipeIds.has(recipe.id)}
                  onClick={() => handleRemoveRecipe(recipe.id)}
                  aria-label={`Remove saved recipe: ${recipe.title}`}
                  title="Remove saved recipe"
                  className="h-8 w-8 shrink-0 rounded-lg text-red-600 hover:text-red-700 sm:h-10 sm:w-10 sm:rounded-md"
                >
                  <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </main>
  )
}

export default SavedRecipes
